import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { alpha } from '@material-ui/core';
import { useSubscription } from '@apollo/client';
import { format } from 'date-fns';
import clamp from 'lodash/clamp';
import Carousel from '@/components/carousel/Carousel';
import PanelContainer from '@/components/Panel/PanelContainer';
import { internalMonitorScheduleSubscription } from '@/graph/procedures';
import { useClock } from '@/components/pages/kiosk/HospitalSchedule.hooks';
import { formatMinutes } from '@/components/pages/kiosk/schedule/shared/procedureUtils';
import { optionalFormatWithTimezone } from '@/components/pages/kiosk/tablet/utils';
import { withTheme } from '@/se/theme';
import type { ScheduleColumnView } from '@/contexts/screenConfiguration.types';
import {
  CARD_HEIGHT_REM,
  CARD_HEIGHT_TALL_REM,
  DEFAULT_SCHEDULE_COLUMN_MAX_COLUMNS,
  DEFAULT_SCHEDULE_COLUMN_VIEW,
  DEFAULT_SCHEDULE_COLUMN_WIDTH_PERCENT,
  LIST_FOOTER_HEIGHT_REM,
  LIST_FOOTER_HEIGHT_TALL_REM,
  MAX_SCHEDULE_COLUMN_MAX_COLUMNS,
  MAX_SCHEDULE_COLUMN_WIDTH_PERCENT,
  MIN_SCHEDULE_COLUMN_MAX_COLUMNS,
  MIN_SCHEDULE_COLUMN_WIDTH_PERCENT,
} from './scheduleColumn.constants';
import {
  buildTokens,
  computeBudget,
  computeGeometry,
  computeScheduleColumnLayout,
  countBelowViewport,
  isTallCard,
  listFooterReservePx,
  packPages,
  packTailPage,
  snapViewportToWholeCard,
} from './scheduleColumnPacking';
import type { ScheduledProcedure, ScheduleToken } from './scheduleColumnPacking';

const CAROUSEL_INTERVAL_MS = 7000;

type ScheduleColumnProps = {
  timezone?: string;
  view?: ScheduleColumnView | null;
  widthPercent?: number | null;
  maxColumns?: number | null;
  groupByTime?: boolean | null;
  showProcedure?: boolean;
  isKiosk?: boolean;
  className?: string;
};

// A stored config value may be null or out of range; coalesce it to the given default, then clamp
// to the admin input bounds. Shared by the width-percent and max-columns settings so the
// coalesce-then-clamp logic lives in one place.
const coalesceAndClamp = (value: number | null | undefined, fallback: number, min: number, max: number) =>
  clamp(value || fallback, min, max);

const clampWidthPercent = (widthPercent?: number | null) =>
  coalesceAndClamp(
    widthPercent,
    DEFAULT_SCHEDULE_COLUMN_WIDTH_PERCENT,
    MIN_SCHEDULE_COLUMN_WIDTH_PERCENT,
    MAX_SCHEDULE_COLUMN_WIDTH_PERCENT
  );

// maxColumns = 1 (the default, and what a missing value coalesces to) is the structural degenerate
// case — one column, no MAX clamp, bit-exact current behavior.
const clampMaxColumns = (maxColumns?: number | null) =>
  coalesceAndClamp(
    maxColumns,
    DEFAULT_SCHEDULE_COLUMN_MAX_COLUMNS,
    MIN_SCHEDULE_COLUMN_MAX_COLUMNS,
    MAX_SCHEDULE_COLUMN_MAX_COLUMNS
  );

// The subscription is opened by mounting this component; the column is only mounted when
// showScheduleColumn is enabled, so disabled screens open no extra subscription. The date
// variable comes from the hospital-timezone clock — when the day rolls over at midnight,
// the changed variable makes Apollo resubscribe with the new date.
const useScheduledProcedures = (timezone?: string) => {
  const date = useClock(timezone);
  const formattedDate = useMemo(() => format(date, 'YYYY-MM-DD'), [date]);
  const { data, loading } = useSubscription(internalMonitorScheduleSubscription, {
    variables: { date: formattedDate },
    shouldResubscribe: true,
  });
  const procedures: ScheduledProcedure[] = data?.internalMonitorSchedule ?? [];
  return { procedures, loading };
};

// Reports the measured content height (packing budget H) and width (the schedule area width the
// fit loop splits into columns) via one ResizeObserver.
const useContentSize = (): [React.RefObject<HTMLDivElement>, number, number] => {
  const contentRef = useRef<HTMLDivElement>(null);
  // Height and width update together in one state object so a resize commits a single render
  // (React 17 does not batch state updates fired outside event handlers). The functional update
  // below bails out (returns the previous object reference unchanged) only for a genuine no-op.
  // Width commits now trigger a re-render (the fit loop needs the live area width) — acceptable
  // because the area has a fixed footprint, so resizes are rare (viewport changes only), not a
  // per-pixel drag.
  const [size, setSize] = useState({ height: 0, width: 0 });

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    const observer = new ResizeObserver(entries => {
      const rect = entries[0].contentRect;
      setSize(prev =>
        prev.height === rect.height && prev.width === rect.width ? prev : { height: rect.height, width: rect.width }
      );
    });
    observer.observe(node);
    const rect = node.getBoundingClientRect();
    setSize(prev =>
      prev.height === rect.height && prev.width === rect.width ? prev : { height: rect.height, width: rect.width }
    );

    return () => observer.disconnect();
  }, []);

  return [contentRef, size.height, size.width];
};

const ScheduleColumn = ({
  timezone,
  view,
  widthPercent,
  maxColumns,
  groupByTime,
  showProcedure,
  isKiosk,
  className,
}: ScheduleColumnProps) => {
  const { procedures, loading } = useScheduledProcedures(timezone);
  const [contentRef, contentHeight, contentWidth] = useContentSize();
  const [scrollTop, setScrollTop] = useState(0);

  // rAF-throttled so scrolling commits at most one state update per frame; the memoized
  // list body means that update re-renders only the footer count, not the cards.
  const latestScrollTop = useRef(0);
  const scrollFrame = useRef<number | null>(null);
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    latestScrollTop.current = event.currentTarget.scrollTop;
    if (scrollFrame.current === null) {
      scrollFrame.current = requestAnimationFrame(() => {
        scrollFrame.current = null;
        setScrollTop(latestScrollTop.current);
      });
    }
  };
  useEffect(
    () => () => {
      if (scrollFrame.current !== null) cancelAnimationFrame(scrollFrame.current);
    },
    []
  );

  const configuredMax = clampMaxColumns(maxColumns);
  const effectiveView = view ?? DEFAULT_SCHEDULE_COLUMN_VIEW;
  // Missing ⇒ ungrouped (the default): no time-slot headers, each card carries its own time.
  const grouped = groupByTime ?? false;
  const listMode = effectiveView === 'LIST';

  const tokens = useMemo(() => buildTokens(procedures, grouped), [procedures, grouped]);
  // The fit search: choose the column count k and split the stream into static prefix pages plus
  // the last column's tokens. At configuredMax = 1 this collapses to one column holding the whole
  // stream — the single-column path, bit-exact. `narrow` and card heights come from the chosen
  // per-column width, not the measured area width.
  const layout = useMemo(
    () =>
      computeScheduleColumnLayout(tokens, contentHeight, contentWidth, configuredMax, effectiveView, isKiosk ?? false),
    [tokens, contentHeight, contentWidth, configuredMax, effectiveView, isKiosk]
  );
  const narrow = layout.narrow;

  const renderToken = useCallback(
    (token: ScheduleToken, index: number) => {
      if (token.kind === 'header') {
        return <TimeSlotHeader key={`header-${token.time}-${index}`}>{token.time}</TimeSlotHeader>;
      }

      // Mirrors the PreOp patient slot (minus the bed section): physician and patient on the
      // prominent row, procedure on the secondary row with the scheduled duration in the dim
      // right slot. Arrived cards additively gain a leading green dot and an "Arrived HH:mm"
      // status chip: on a wide (non-tall) card the chip sits inline in the prominent row, right
      // before the patient name; on a narrow (tall) card there isn't room, so the chip drops to
      // its own line directly under the prominent row (row 2 of 3), with the secondary row
      // (procedure + duration — identical for arrived and non-arrived cards) as row 3. Non-arrived
      // cards are unchanged.
      const { procedure } = token;
      const patient = procedure.patientName ?? '–';
      const duration = procedure.duration ? formatMinutes(procedure.duration) : null;
      const arrived = procedure.arrivedAt != null;
      const arrivedTime = optionalFormatWithTimezone(procedure.arrivedAt, 'HH:mm', null);
      const tall = isTallCard(procedure, narrow);
      return (
        <Card key={procedure.id} $arrived={arrived} $tall={tall}>
          <CardMain>
            {arrived && <ArrivedDot />}
            {/* Ungrouped: the scheduled time leads the prominent row (after the arrived dot),
                mirroring the time-slot header the ungrouped mode drops. Grouped mode keeps the
                header and renders no per-card time. */}
            {!grouped && <CardTime>{procedure.startTimeText ?? '–'}</CardTime>}
            <CardPhysician>{procedure.physicianName ?? '–'}</CardPhysician>
            {arrived && !tall && <ArrivedBadge>Arrived {arrivedTime}</ArrivedBadge>}
            <CardPatient>{patient}</CardPatient>
          </CardMain>
          {arrived && tall && (
            <CardArrivedRow>
              <ArrivedBadge>Arrived {arrivedTime}</ArrivedBadge>
            </CardArrivedRow>
          )}
          <CardSub>
            <CardProcedure>{showProcedure ? procedure.procedureType : null}</CardProcedure>
            <CardDuration>{duration}</CardDuration>
          </CardSub>
        </Card>
      );
    },
    [showProcedure, narrow, grouped]
  );

  // Footer geometry and the carousel budget are scoped to the last column (the only overflowing
  // one); prefix columns are static full pages.
  const geometry = useMemo(() => computeGeometry(layout.lastColumnTokens, narrow), [layout.lastColumnTokens, narrow]);
  const budget = computeBudget(contentHeight);

  // Columns 1..k−1 are static packed pages, rendered identically in both view modes.
  const prefixColumns = useMemo(
    () =>
      layout.prefixPages.map((page, index) => (
        <ColumnCell key={`prefix-${index}`} $widthPx={layout.columnWidthPx} $first={index === 0}>
          {page.map(renderToken)}
        </ColumnCell>
      )),
    [layout.prefixPages, layout.columnWidthPx, renderToken]
  );

  const listBody = useMemo(
    () => (listMode ? layout.lastColumnTokens.map(renderToken) : null),
    [listMode, layout.lastColumnTokens, renderToken]
  );
  const carouselBody = useMemo(() => {
    if (listMode) return null;
    const pages = packPages(layout.lastColumnTokens, budget, narrow);
    if (pages.length > 1) {
      pages[pages.length - 1] = packTailPage(layout.lastColumnTokens, budget, narrow);
    }
    return pages.map((page, pageIndex) => <PageColumn key={pageIndex}>{page.map(renderToken)}</PageColumn>);
  }, [listMode, layout.lastColumnTokens, budget, narrow, renderToken]);

  let columns: React.ReactNode;
  if (procedures.length === 0) {
    columns = !loading && <Empty>All scheduled patients have been admitted</Empty>;
  } else {
    // On kiosks a footer counts the cards not visible below the last column's scroll viewport,
    // live-updating as it scrolls. It lives inside the last column's cell (bottom-pinned, width =
    // the cell): only that cell's scroll viewport shrinks by the reserved footer height, while
    // static prefix columns keep the full measured height — the exact capacity the fit model
    // already assumes (H − the resolved footer reserve). The reserve is narrow-dependent and
    // resolved from the same `narrow` the fit search used (layout.narrow), so it matches the reserve
    // the packer already subtracted. The visibility test is unchanged (overflow of the full measured
    // H). The scroll viewport is then snapped DOWN to end on a whole card's bottom (never mid-card at
    // rest); the leftover between the snapped height and H − reserve collapses into the gap above the
    // footer. `countBelowViewport` runs against the snapped height so `+N more` matches the whole
    // cards actually visible.
    const footerShown = listMode && isKiosk && contentHeight > 0 && geometry.totalHeight > contentHeight;
    const snappedViewport = footerShown
      ? snapViewportToWholeCard(layout.lastColumnTokens, narrow, contentHeight - listFooterReservePx(narrow))
      : undefined;

    let footer: React.ReactNode = null;
    if (footerShown && snappedViewport != null) {
      const belowViewport = countBelowViewport(geometry.cardTops, scrollTop, snappedViewport);
      const lastCaseTime = procedures[procedures.length - 1].startTimeText;
      const moreText = belowViewport > 0 ? `+${belowViewport} more` : '';
      const lastCaseText = lastCaseTime ? `last case ${lastCaseTime}` : '';
      footer = (
        // Pinned height is a pure function of `narrow` (never of the text), so the viewport above
        // cannot flap as the count collapses. Narrow stacks the text as two deliberate lines so it
        // can never wrap-overflow the pinned block.
        <FooterGroup $narrow={narrow}>
          {/* visibility (not conditional render) so the reserved footer area never shifts */}
          <FooterEllipsis $visible={belowViewport > 0}>⋯</FooterEllipsis>
          {narrow ? (
            <FooterCard>
              {moreText ? <FooterLine>{moreText}</FooterLine> : null}
              {lastCaseText ? <FooterLine>{lastCaseText}</FooterLine> : null}
            </FooterCard>
          ) : (
            <FooterCard>
              {moreText}
              {moreText && lastCaseText ? ' · ' : ''}
              {lastCaseText}
            </FooterCard>
          )}
        </FooterGroup>
      );
    }

    // Only the last column overflows — LIST scrolls it, CAROUSEL rotates its remaining pages.
    // When content fits in ≤ k columns, both modes render this column identically (no scroll, no
    // rotation). Kiosk LIST nests the scroll in a ScrollArea so the in-cell footer stays pinned
    // below it (a stable scroller across the footer appearing/disappearing); browser LIST keeps
    // scrolling on the cell itself (no footer), and CAROUSEL clips (the carousel paginates).
    const kioskList = listMode && isKiosk;
    const lastColumn = (
      <ColumnCell
        key="last"
        $widthPx={layout.columnWidthPx}
        $first={layout.prefixPages.length === 0}
        $scrollable={listMode && !isKiosk}
        $footerHost={kioskList}
        onScroll={listMode && !isKiosk ? handleScroll : undefined}
      >
        {listMode ? (
          kioskList ? (
            <ListScrollArea onScroll={handleScroll} $heightPx={snappedViewport}>
              {listBody}
            </ListScrollArea>
          ) : (
            listBody
          )
        ) : (
          <Carousel size={1} interval={CAROUSEL_INTERVAL_MS}>
            {carouselBody}
          </Carousel>
        )}
        {footer}
      </ColumnCell>
    );
    columns = (
      <>
        {prefixColumns}
        {lastColumn}
      </>
    );
  }

  return (
    <Column className={className} $widthPercent={clampWidthPercent(widthPercent)}>
      <PanelContainer title="Scheduled" patients={procedures.length}>
        {/* Fixed-footprint area the columns split, left-anchored; leftover stays empty. */}
        <Content ref={contentRef}>{columns}</Content>
      </PanelContainer>
    </Column>
  );
};

const Column = styled.div<{ $widthPercent: number }>`
  display: flex;
  flex-flow: column;
  flex: 0 0 ${props => props.$widthPercent}vw;
  min-width: 0;
  padding: 0.5rem 0 1rem 1rem;
`;

// The fixed-footprint schedule area: a left-anchored row of column cells. Both axes are clipped
// here; the last cell owns the vertical scroll (kiosk LIST nests it in a ScrollArea so its
// bottom-pinned footer stays put). Columns are sized in px by the fit loop, so any leftover on
// the right stays empty (the day-draining signal) with the area boundary kept. Its measured
// height is the packing budget H and is unperturbed by the footer, which lives inside a cell.
const Content = styled.div`
  display: flex;
  flex-flow: row;
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;

// One schedule column. Width is the fit loop's per-column px (with a full-fill fallback before
// the area is measured, so the single-column case never flashes a zero-width cell); the inter-
// column gutter is border-box padding so k cells sum to exactly the area width. Prefix cells are
// static and clipped. The last cell scrolls in LIST mode: browser LIST scrolls the cell directly
// ($scrollable); kiosk LIST is a $footerHost — it clips and delegates scrolling to an inner
// ScrollArea so the bottom-pinned footer stays fixed, so its gap collapses to 0 (its children
// are the scroll area and the footer, not cards — the 0.5rem inter-card gap lives in ScrollArea).
const ColumnCell = styled.div<{ $widthPx: number; $first?: boolean; $scrollable?: boolean; $footerHost?: boolean }>`
  flex: ${props => (props.$widthPx > 0 ? `0 0 ${props.$widthPx}px` : '1 1 0')};
  box-sizing: border-box;
  display: flex;
  flex-flow: column;
  min-height: 0;
  min-width: 0;
  gap: ${props => (props.$footerHost ? '0' : '0.5rem')};
  padding-left: ${props => (props.$first ? '0' : '1rem')};
  overflow-x: hidden;
  overflow-y: ${props => (props.$scrollable ? 'auto' : 'hidden')};
`;

// The kiosk-LIST scroll viewport nested inside the last cell so the footer can pin below it
// without scrolling. Carries the inter-card gap the cell would otherwise provide. When the footer
// is hidden it grows to fill the cell (flex: 1, real viewport H). When the footer shows it takes an
// explicit snapped height ($heightPx = the whole-card-snapped viewport) instead of flexing, so the
// bottom edge lands on a whole card; the leftover between it and the footer collapses into the gap
// the footer's margin-top:auto opens above the footer.
const ListScrollArea = styled.div<{ $heightPx?: number }>`
  flex: ${props => (props.$heightPx != null ? '0 0 auto' : '1 1 0')};
  ${props =>
    props.$heightPx != null &&
    css`
      height: ${props.$heightPx}px;
    `}
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-flow: column;
  gap: 0.5rem;
  overflow-x: hidden;
  overflow-y: auto;
`;

const PageColumn = styled.div`
  display: flex;
  flex-flow: column;
  gap: 0.5rem;
`;

const TimeSlotHeader = styled.div`
  flex: 0 0 auto;
  height: 1.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${withTheme((theme: any) => theme.textColor.string())};
`;

const ArrivedDot = styled.span`
  flex: 0 0 auto;
  align-self: center;
  width: 0.6em;
  height: 0.6em;
  border-radius: 50%;
  background-color: ${withTheme((theme: any) => theme.successColor.string())};
`;

const Card = styled.div<{ $arrived?: boolean; $tall?: boolean }>`
  flex: 0 0 auto;
  height: ${props => (props.$tall ? `${CARD_HEIGHT_TALL_REM}rem` : `${CARD_HEIGHT_REM}rem`)};
  box-sizing: border-box;
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  background-color: ${withTheme((theme: any) => alpha(theme.secondaryTextColor.string(), 0.05))};
  overflow: hidden;
  ${props =>
    props.$arrived &&
    css`
      background-color: ${withTheme((theme: any) => alpha(theme.successColor.string(), 0.08))};
      border: 0.125rem solid ${withTheme((theme: any) => alpha(theme.successColor.string(), 0.5))};
    `}
`;

const CardMain = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 1.5em;
  line-height: 1.4;
  color: ${withTheme((theme: any) => theme.textColor.string())};
`;

// The per-card scheduled time in ungrouped mode: a distinct, header-like label leading the
// prominent row. Sized down from the row's 1.5em to echo the (dropped) time-slot header's smaller
// proportion, weighted like it, and baseline-aligned with the physician name so it reads as a label
// rather than a second name. Fixed-width and nowrap (times are short) so it never truncates; the
// physician name keeps flex: 1 and its ellipsis. Adds no row (single line) so card height is
// unchanged in both the wide 2-row and narrow 3-row layouts.
const CardTime = styled.div`
  flex: 0 0 auto;
  align-self: baseline;
  white-space: nowrap;
  font-size: 0.85em;
  font-weight: 600;
  color: ${withTheme((theme: any) => theme.secondaryTextColor.string())};
`;

const CardPhysician = styled.div`
  flex: 1 1 0;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardPatient = styled.div`
  flex: 0 0 auto;
  white-space: nowrap;
`;

const CardSub = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 1.125rem;
  line-height: 1.1;
  color: ${withTheme((theme: any) => theme.secondaryTextColor.string())};
`;

const CardProcedure = styled.div`
  flex: 1 1 0;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardDuration = styled.div`
  flex: 0 0 auto;
  white-space: nowrap;
  opacity: 0.5;
`;

// The "Arrived HH:mm" status chip. Sized down from the prominent row's 1.5em to match CardTime's
// scale, baseline-aligned so it reads as a label sitting in the main row rather than a second
// name; fixed to its content (never wraps or truncates — the physician name's flex/ellipsis
// truncates first). Reused, unstyled-changed, inside CardArrivedRow for the narrow (tall) card's
// own line, where that wrapper's matching 1.5em font-size keeps the chip the same absolute size
// as the inline wide-card instance.
const ArrivedBadge = styled.div`
  flex: 0 0 auto;
  align-self: baseline;
  white-space: nowrap;
  font-size: 0.85em;
  font-weight: 500;
  color: ${withTheme((theme: any) => theme.successColor.string())};
`;

// Narrow (tall) arrived cards only: the Arrived badge's own line (row 2 of 3), directly under the
// prominent row and above the procedure/duration row. font-size matches CardMain (1.5em) purely so
// ArrivedBadge's 0.85em resolves to the same absolute size as its inline placement on wide cards.
const CardArrivedRow = styled.div`
  flex: 0 0 auto;
  font-size: 1.5em;
  line-height: 1.4;
`;

// Pinned at the bottom of the last column's cell (margin-top:auto pushes it down so the leftover
// from the snapped scroll viewport collapses into the gap above it). Its rendered height is fixed to
// the narrow-appropriate rem constant (border-box) so it equals the packer's resolved reserve
// exactly — the amount the fit math reserves and the amount the scroll viewport above it gives up.
// The height is a pure function of `narrow`, never of the footer text, so the viewport above cannot
// flap. overflow:hidden is a backstop against any future content growth spilling past the block.
const FooterGroup = styled.div<{ $narrow: boolean }>`
  flex: 0 0 auto;
  box-sizing: border-box;
  margin-top: auto;
  height: ${props => (props.$narrow ? LIST_FOOTER_HEIGHT_TALL_REM : LIST_FOOTER_HEIGHT_REM)}rem;
  display: flex;
  flex-flow: column;
  gap: 0.25rem;
  padding-top: 0.25rem;
  overflow: hidden;
`;

const FooterEllipsis = styled.div<{ $visible: boolean }>`
  text-align: center;
  line-height: 1;
  font-size: 1.5rem;
  letter-spacing: 0.25em;
  opacity: 0.6;
  color: ${withTheme((theme: any) => theme.secondaryTextColor.string())};
  visibility: ${props => (props.$visible ? 'visible' : 'hidden')};
`;

const FooterCard = styled.div`
  border-radius: 4px;
  padding: 0.75rem;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 500;
  color: ${withTheme((theme: any) => theme.textColor.string())};
  background-color: ${withTheme((theme: any) => alpha(theme.secondaryTextColor.string(), 0.12))};
`;

// One deliberate line of the narrow (two-line) footer. Kept single-line with ellipsis so a very
// narrow column degrades to a clipped line rather than wrapping and overflowing the pinned block.
const FooterLine = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Empty = styled.div`
  margin: auto;
  opacity: 0.7;
  text-align: center;
  color: ${withTheme((theme: any) => theme.textColor.string())};
`;

export default React.memo(ScheduleColumn);
